import { z } from "zod";
import { IsoDateTimeSchema } from "./common.js";

/** Hermes 官方 Skills 清单的安全来源分类。 */
export const SkillProvenanceSchema = z.enum(["hub", "bundled", "agent"]);
export const SkillNameSchema = z
  .string()
  .trim()
  .min(1)
  .max(200)
  .regex(/^[A-Za-z0-9][A-Za-z0-9._-]*$/, "技能名称不合法");

/**
 * 浏览器只接收渐进披露第一层元数据。这里刻意没有 path、content、files、
 * config 或环境变量字段，防止管理页演变为服务器文件浏览器。
 */
export const SkillCatalogItemSchema = z
  .object({
    name: SkillNameSchema,
    description: z.string().trim().max(4_000),
    category: z.string().trim().min(1).max(120).nullable(),
    enabled: z.boolean(),
    usage: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER),
    provenance: SkillProvenanceSchema,
  })
  .strict();

export const SkillCatalogSchema = z
  .object({
    items: z.array(SkillCatalogItemSchema).max(2_000),
    count: z.number().int().nonnegative().max(2_000),
    enabledCount: z.number().int().nonnegative().max(2_000),
    disabledCount: z.number().int().nonnegative().max(2_000),
    updatedAt: IsoDateTimeSchema,
  })
  .strict()
  .superRefine((catalog, context) => {
    if (catalog.count !== catalog.items.length) {
      context.addIssue({
        code: "custom",
        path: ["count"],
        message: "技能总数与目录条目数量不一致",
      });
    }
    if (catalog.enabledCount + catalog.disabledCount !== catalog.count) {
      context.addIssue({
        code: "custom",
        path: ["enabledCount"],
        message: "启用与停用数量之和必须等于技能总数",
      });
    }
  });

/**
 * Skill 正文由 Hermes 官方只读接口按名称加载。HerSkin 在服务端完成路径与凭证
 * 脱敏，浏览器不会接触技能目录位置、关联文件或任意文件读取参数。
 */
export const SkillDocumentSchema = z
  .object({
    name: SkillNameSchema,
    content: z.string().min(1).max(300_000),
    redacted: z.literal(true),
    updatedAt: IsoDateTimeSchema,
  })
  .strict();

export type SkillProvenance = z.infer<typeof SkillProvenanceSchema>;
export type SkillCatalogItem = z.infer<typeof SkillCatalogItemSchema>;
export type SkillCatalog = z.infer<typeof SkillCatalogSchema>;
export type SkillDocument = z.infer<typeof SkillDocumentSchema>;
